import { map } from 'lodash-es'
import moment from 'moment'

import $router from 'src/router'
import { toFixedNumber } from 'src/utils/common'

const initFormPrimitive = {
  campaign: '',
  actualBudget: 0,
  plannedBudget: 0,
  coop: 0,
  description: '',
  strategy: '',
  notes: '',
  files: {
    invoices: [],
    planner: [],
  },
}
const initState = () => ({
  showModal: false,
  loading: false,
  fileUploading: false,
  errors: {},
  options: {
    productName: '',
    productId: null,
    expenseId: null,
    categoryId: null,
  },
  lastMonthExpenses: [],
  form: {
    ...initFormPrimitive,
    date: [],
    saved: {
      ...initFormPrimitive,
      date: [],
    },
  },
})

export default {
  namespaced: true,
  state: initState(),
  mutations: {
    SET_LAST_MONTH_EXPENSES(state, payload) {
      state.lastMonthExpenses = payload
    },
    SET_ACTIVE_MODAL(state, payload) {
      state.showModal = payload
    },
    SET_FORM(state, payload) {
      state.form = payload
    },
    SET_SAVED(state, payload = {}) {
      state.form.saved = {
        ...state.form.saved,
        ...payload,
      }
    },
    SET_OPTIONS(state, payload) {
      state.options = payload
    },
    SET_LOADING(state, payload) {
      state.loading = payload
    },
    SET_FILE_UPLOADING(state, payload) {
      state.fileUploading = payload
    },
    SET_DATE(state, { index, value }) {
      const newDate = [...state.form.date]
      newDate[index] = { ...newDate[index], ...value }
      state.form.date = newDate
    },
    SET_ERRORS(state, payload) {
      state.errors = {
        ...state.errors,
        ...payload,
      }
    },
    CLEAR_ERRORS(state) {
      state.errors = {}
    },
    ADD_FLIGHT_DATE(state, payload) {
      state.form.date = [...state.form.date, payload]
    },
    REMOVE_FLIGHT_DATE(state, i) {
      const newDate = [...state.form.date]
      newDate.splice(i, 1)
      state.form.date = newDate
    },
  },
  actions: {
    createExpense({ state, dispatch, commit, rootState }, body) {
      const { options } = state
      const dealerShipId = options.dealerShipId || rootState.settings.dealerShipId
      const userParams = {
        dealerShipId,
      }
      if (dealerShipId) {
        return dispatch('expense/create', { body, userParams }, { root: true })
          .then((data) => {
            const { expense } = data
            commit('SET_OPTIONS', {
              ...options,
              expenseId: expense.id,
            })
            return data
          })
          .catch((e) => {
            throw e
          })
      }
    },
    updateExpense({ dispatch }, body) {
      return dispatch('expense/update', { body }, { root: true }).then((response) => response)
    },
    createProduct({ state, dispatch, commit }, body) {
      return dispatch('budget/createProduct', body, { root: true }).then((data) => {
        const { productId } = data
        commit('SET_OPTIONS', { ...state.options, productId })
        return data
      })
    },
    getExpense({ dispatch }, id) {
      return dispatch('expense/get', id, { root: true })
    },
    getAllExpenses({ dispatch, rootGetters }, { categoryId, productId, exclude = [] }) {
      const subproducts = rootGetters['budget/getSubproducts'](categoryId, productId)
      return Promise.all(
        subproducts
          .filter(({ title }) => !exclude.includes(title))
          .map(async ({ expenses }) => {
            const data = await dispatch('getExpense', expenses[0].id)
            return data
          })
      )
    },
    async saveProduct({ state, rootState, dispatch, getters, commit }) {
      const { month, year } = rootState.settings
      const { form, options } = state
      const files = form.files?.planner.map((file) => ({
        fileId: file.id,
        filename: file.filename,
        keepEachMonth: file.keepEachMonth,
      }))
      const { productName, categoryId, expenseId } = options
      let { productId } = options

      const { strategy, description, notes, date, coop, actualBudget, campaign } = form
      const { targetBudget } = getters
      const formattedMonth = moment().month(month).format('MM')
      const formatDate = (days) => moment().year(year).month(month).date(days).format('DD')
      const flightDate = map(date, (item) => ({
        ...item,
        start: `${year}-${formattedMonth}-${formatDate(item.start)}`,
        end: `${year}-${formattedMonth}-${formatDate(item.end)}`,
      }))
      const saveExpense = async () => {
        const expenseBody = {
          strategy,
          notes,
          flightDate,
          actualBudget,
          description,
          coop,
          campaign,
          targetBudget,
          files,
        }
        const createExpense = ({ productId }) =>
          dispatch('createExpense', {
            productId,
            productCategoryId: categoryId,
            ...expenseBody,
          })
        if (expenseId) {
          return dispatch('updateExpense', {
            id: expenseId,
            ...expenseBody,
          })
        } else if (productId) {
          return createExpense({ productId })
        }

        if (!productId) {
          productId = (
            await dispatch('createProduct', {
              name: productName,
              strategy,
              description,
              productCategoryId: categoryId,
            })
          ).productId
        }

        return createExpense({ productId })
      }
      commit('CLEAR_ERRORS')
      try {
        await dispatch('validate')
        const expense = await saveExpense()
        dispatch('budget/getCategories', null, { root: true })
        return expense
      } catch (e) {
        const errors = e.errors
        if (errors || e.response.data.errors) {
          const error = errors ? errors[0] : e.response.data.errors[0]
          commit('SET_ERRORS', {
            [error.field]: error,
          })
        } else {
          throw e
        }
      }
    },
    getFormattedExpense({ state, dispatch, commit }) {
      commit('SET_LOADING', true)
      const { options } = state
      const { expenseId } = options
      return dispatch('getExpense', expenseId).then((expense) => {
        const date = expense.flightDate.map((date) => ({
          ...date,
          start: date.start.day,
          end: date.end.day,
        }))
        const form = {
          ...expense,
          date,
        }
        commit('SET_FORM', {
          ...state.form,
          ...form,
        })
        commit('SET_SAVED', form)
        commit('SET_LOADING', false)
      })
    },
    async openModal(
      { commit, state, dispatch, getters },
      { form = {}, options = {}, needRouteChange = true }
    ) {
      commit('CLEAR_ERRORS')
      commit('SET_ACTIVE_MODAL', true)
      commit('SET_SCROLL', false, { root: true })

      if (needRouteChange) {
        $router.toggleQueryParams({
          add: {
            category: options.categoryId,
            expense: options.expenseId,
            product: options.productId,
            expenseModal: 'open',
          },
        })
      }

      commit('SET_OPTIONS', {
        ...state.options,
        ...options,
      })
      commit('SET_FORM', {
        ...state.form,
        ...form,
      })
      commit('SET_SAVED', {
        ...form,
      })
      if (options.expenseId) {
        await dispatch('getFormattedExpense')
      }
      if (!state.form.date.length) {
        commit('ADD_FLIGHT_DATE', getters.defaultDateItem)
        commit('SET_SAVED', {
          date: [getters.defaultDateItem],
        })
      }
    },
    closeModal({ commit, dispatch }) {
      commit('SET_ACTIVE_MODAL', false)
      $router.toggleQueryParams({ remove: ['category', 'expense', 'product', 'expenseModal'] })
      dispatch('CLEAR')
      commit('SET_SCROLL', true, { root: true })
    },
    reset({ state, dispatch, commit }) {
      if (Object.keys(state.form.saved).length) {
        for (const key in state.form.saved) {
          if (Object.hasOwnProperty.call(state.form.saved, key)) {
            commit('SET_FORM', {
              ...state.form,
              [key]: state.form.saved[key],
            })
          }
        }
      } else {
        dispatch('CLEAR')
      }
      commit('CLEAR_ERRORS')
    },
    CLEAR({ commit }) {
      const { options, form } = initState()
      commit('SET_OPTIONS', options)
      commit('SET_FORM', form)
    },
    validate({ state }) {
      const { form } = state
      const { date } = form
      const throwValidationError = (message, field = null, payload) => {
        throw {
          errors: [
            {
              message,
              field,
              ...payload,
            },
          ],
        }
      }
      const dateOverlap = (dateItem, itemIndex) =>
        date.some(
          ({ start, end }, i) =>
            i !== itemIndex &&
            ((start >= dateItem.start && start <= dateItem.end) ||
              (end <= dateItem.end && end >= dateItem.start))
        )
      for (const [key, item] of date.entries()) {
        if (dateOverlap(item, key)) {
          throwValidationError('The flight dates can not have the same dates.', 'flightDate')
        }
      }
    },
  },
  getters: {
    targetBudget(state) {
      return toFixedNumber(
        state.form.date.reduce((acc, item) => acc + Number(item.targetBudget), 0),
        2
      )
    },
    defaultDateItem(state, getters, rootState) {
      let { month, year } = rootState.settings

      if (!month && !year) {
        const settings = JSON.parse(localStorage.getItem('settings'))
        month = settings?.month || new Date().getMonth() + 1
        year = settings?.year || new Date().getFullYear()
      }

      return {
        targetBudget: 0,
        start: 1,
        end: moment().month(month).year(year).daysInMonth(),
      }
    },
    changed(state, getters) {
      const result = []
      const isChanged = (key) => {
        const input = state.form[key]
        const savedInput = state.form.saved[key]
        const types = {
          plannedBudget(input) {
            return input !== getters.targetBudget
          },
        }
        if (savedInput) {
          if (Array.isArray(input)) {
            return input.some((item, i) =>
              Object.entries(item).some(([key, value]) =>
                savedInput[i] ? savedInput[i][key] !== value : true
              )
            )
          }
          return types[key] ? types[key](savedInput) : savedInput !== input
        }
        if (Array.isArray(input)) {
          return input.some((item) => Object.values(item).some((value) => !!value))
        }
        return !!input
      }
      for (const key in state.form) {
        if (key === 'saved') {
          continue
        }
        if (key === 'files') {
          if (
            JSON.stringify(state.form.saved.files.planner) !==
            JSON.stringify(state.form.files?.planner)
          ) {
            result.push({ name: key })
          }
          continue
        }
        if (isChanged(key)) {
          result.push({ name: key })
        }
      }
      return result
    },
  },
}
